
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { isAdmin as checkIsAdmin } from "@/services/authService";
import AdminLayout from "@/components/admin/AdminLayout";
import PostEditor from "@/components/admin/PostEditor";
import PostLoading from "@/components/admin/PostLoading";
import { usePostEditor } from "@/hooks/usePostEditor";

const EditPost = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const {
    title,
    slug,
    excerpt,
    content,
    author,
    seoDescription,
    theme,
    categories,
    featuredImage,
    featuredImageAlt,
    featuredImageTitle,
    isDraft,
    isPending,
    isLoading,
    showDeleteDialog,
    setShowDeleteDialog,
    setTitle,
    setSlug,
    setExcerpt,
    setContent,
    setAuthor,
    setSeoDescription,
    setTheme,
    setCategories,
    setFeaturedImage,
    setFeaturedImageAlt,
    setFeaturedImageTitle,
    togglePublishStatus,
    handleImageUpload,
    handleDeletePost,
    handleSubmit,
  } = usePostEditor(id);

  // Check if the user is logged in
  useEffect(() => {
    const checkSession = async () => {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) {
        toast({
          title: "Access denied",
          description: "Please login to edit blog posts",
          variant: "destructive",
        });
        
        navigate("/admin/login");
      }
    };
    
    checkSession();
  }, [navigate, toast]);

  if (isLoading) {
    return <PostLoading />;
  }

  return (
    <AdminLayout title="Edit Blog Post" backLink="/admin/blog" backLabel="Back to Blog Management">
      <PostEditor
        id={id}
        title={title}
        setTitle={setTitle}
        slug={slug}
        setSlug={setSlug}
        excerpt={excerpt}
        setExcerpt={setExcerpt}
        author={author}
        setAuthor={setAuthor}
        seoDescription={seoDescription}
        setSeoDescription={setSeoDescription}
        theme={theme}
        setTheme={setTheme}
        categories={categories}
        setCategories={setCategories}
        content={content}
        setContent={setContent}
        featuredImage={featuredImage}
        setFeaturedImage={setFeaturedImage}
        featuredImageAlt={featuredImageAlt}
        setFeaturedImageAlt={setFeaturedImageAlt}
        featuredImageTitle={featuredImageTitle}
        setFeaturedImageTitle={setFeaturedImageTitle}
        isDraft={isDraft}
        isPending={isPending}
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        togglePublishStatus={togglePublishStatus}
        handleSubmit={handleSubmit}
        handleDeletePost={handleDeletePost}
        onFileUpload={handleImageUpload}
      />
    </AdminLayout>
  );
};

export default EditPost;
